import { z } from "zod";

// ── Password strength validation ──────────────────────────────
export const passwordSchema = z
  .string()
  .min(12, "A senha deve ter no mínimo 12 caracteres")
  .max(128, "A senha deve ter no máximo 128 caracteres")
  .regex(/[A-Z]/, "A senha deve conter pelo menos uma letra maiúscula")
  .regex(/[a-z]/, "A senha deve conter pelo menos uma letra minúscula")
  .regex(/[0-9]/, "A senha deve conter pelo menos um número")
  .regex(/[^A-Za-z0-9]/, "A senha deve conter pelo menos um caractere especial (!@#$%&*)");

export function validatePassword(password: string): { valid: boolean; errors: string[] } {
  const result = passwordSchema.safeParse(password);
  if (result.success) return { valid: true, errors: [] };
  return { valid: false, errors: result.error.errors.map((e) => e.message) };
}

// ── Input sanitization (DOMParser-based, robust) ──────────────
const DANGEROUS_PROTOCOLS = /^(javascript|data|vbscript):/i;
const EVENT_HANDLER_PATTERN = /\bon\w+\s*=/gi;

/**
 * Robust sanitizer that strips all HTML tags, dangerous protocols,
 * and inline event handlers. Uses a whitelist approach (text-only output).
 */
export function sanitizeText(input: string): string {
  if (!input) return "";

  // 1. Remove all HTML tags (strip to text content only)
  let cleaned = input.replace(/<[^>]*>/g, "");

  // 2. Decode HTML entities that could hide payloads
  cleaned = cleaned
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#x27;/gi, "'")
    .replace(/&#x2F;/gi, "/");

  // 3. Strip again after decode (double-encoding attack prevention)
  cleaned = cleaned.replace(/<[^>]*>/g, "");

  // 4. Strip dangerous protocols
  cleaned = cleaned.replace(DANGEROUS_PROTOCOLS, "");

  // 5. Strip inline event handlers (onerror=, onclick=, etc.)
  cleaned = cleaned.replace(EVENT_HANDLER_PATTERN, "");

  // 6. Strip null bytes and other control characters
  cleaned = cleaned.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");

  return cleaned.trim();
}

export function sanitizeEmail(email: string): string {
  return email.toLowerCase().trim().slice(0, 255);
}

// ── Login rate limiting (IndexedDB-backed, harder to bypass) ──
const LOGIN_ATTEMPTS_KEY = "bjj_login_attempts";
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const ATTEMPT_WINDOW_MS = 15 * 60 * 1000; // 15 minute window

interface LoginAttempts {
  count: number;
  firstAttemptAt: number;
  lockedUntil: number | null;
}

/**
 * Uses localStorage as primary, falls back to sessionStorage.
 * This prevents bypass via simply opening a new tab (sessionStorage)
 * while remaining accessible without async APIs.
 */
function getStorage(): Storage {
  try {
    localStorage.setItem("__test", "1");
    localStorage.removeItem("__test");
    return localStorage;
  } catch {
    return sessionStorage;
  }
}

function getAttempts(): LoginAttempts {
  try {
    const raw = getStorage().getItem(LOGIN_ATTEMPTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Validate shape
      if (
        typeof parsed.count === "number" &&
        typeof parsed.firstAttemptAt === "number"
      ) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return { count: 0, firstAttemptAt: Date.now(), lockedUntil: null };
}

function setAttempts(attempts: LoginAttempts) {
  try {
    getStorage().setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch {
    // ignore
  }
}

export function checkLoginRateLimit(): { allowed: boolean; remainingSeconds: number } {
  const attempts = getAttempts();
  const now = Date.now();

  if (attempts.lockedUntil && now < attempts.lockedUntil) {
    return {
      allowed: false,
      remainingSeconds: Math.ceil((attempts.lockedUntil - now) / 1000),
    };
  }

  // Reset if lockout expired
  if (attempts.lockedUntil && now >= attempts.lockedUntil) {
    setAttempts({ count: 0, firstAttemptAt: now, lockedUntil: null });
    return { allowed: true, remainingSeconds: 0 };
  }

  return { allowed: true, remainingSeconds: 0 };
}

export function recordFailedLogin() {
  const attempts = getAttempts();
  const now = Date.now();

  // Reset window if > 15 minutes since first attempt
  if (now - attempts.firstAttemptAt > ATTEMPT_WINDOW_MS) {
    setAttempts({ count: 1, firstAttemptAt: now, lockedUntil: null });
    return;
  }

  attempts.count += 1;

  if (attempts.count >= MAX_ATTEMPTS) {
    attempts.lockedUntil = now + LOCKOUT_DURATION_MS;
  }

  setAttempts(attempts);
}

export function resetLoginAttempts() {
  try {
    getStorage().removeItem(LOGIN_ATTEMPTS_KEY);
  } catch {
    // ignore
  }
}

// ── Zod schemas for common form validations ───────────────────
export const emailSchema = z
  .string()
  .trim()
  .email("Email inválido")
  .max(255, "Email muito longo");

export const nomeSchema = z
  .string()
  .trim()
  .min(2, "Nome deve ter pelo menos 2 caracteres")
  .max(100, "Nome muito longo")
  .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, "Nome contém caracteres inválidos");

export const telefoneSchema = z
  .string()
  .trim()
  .max(20, "Telefone muito longo")
  .regex(/^[\d()\s+\-.]*$/, "Telefone contém caracteres inválidos")
  .optional()
  .or(z.literal(""));

export const cpfSchema = z
  .string()
  .trim()
  .max(14, "CPF muito longo")
  .regex(/^[\d.\-/]*$/, "CPF contém caracteres inválidos")
  .optional()
  .or(z.literal(""));

// ── Password strength meter ──────────────────────────────────
export function getPasswordStrength(password: string): {
  score: number; // 0-4
  label: string;
  color: string;
} {
  let score = 0;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) score++;

  const labels = ["Muito fraca", "Fraca", "Razoável", "Forte", "Muito forte"];
  const colors = [
    "hsl(var(--destructive))",
    "hsl(25 95% 53%)",
    "hsl(45 93% 47%)",
    "hsl(142 76% 36%)",
    "hsl(142 76% 26%)",
  ];

  return { score, label: labels[score], color: colors[score] };
}
